/**
 * Presentation-only BlissNet branded full-screen loading experience.
 * No auth, profile fetch, navigation, or timers — callers own those.
 */
import React from 'react';
import { BrandLogo } from '@/components/BrandLogo';

export type BlissNetLoadingScreenMode = 'loading' | 'slow' | 'error';

export interface BlissNetLoadingScreenProps {
  title: string;
  message: string;
  mode?: BlissNetLoadingScreenMode;
  actions?: React.ReactNode;
  /** Keep title/message English LTR even when document dir is RTL */
  isolateEnglishText?: boolean;
  /** Screen-reader label for the spinner. Defaults to English so existing callers stay unchanged. */
  loadingLabel?: string;
  /** Extra classes on the root fixed container (e.g. z-index for boot splash) */
  className?: string;
}

export function BlissNetLoadingScreen({
  title,
  message,
  mode = 'loading',
  actions,
  isolateEnglishText = false,
  loadingLabel = 'Loading',
  className = '',
}: BlissNetLoadingScreenProps) {
  const messageClassName =
    mode === 'error'
      ? 'text-base sm:text-lg text-rose-300/90'
      : 'text-lg sm:text-xl text-zinc-400';

  const showSpinner = mode !== 'error';

  const englishIsolateStyle = {
    direction: 'ltr' as const,
    unicodeBidi: 'isolate' as const,
  };

  const englishTextProps = isolateEnglishText
    ? {
        dir: 'ltr' as const,
        lang: 'en' as const,
        style: englishIsolateStyle,
      }
    : {};

  const rootLocaleProps = isolateEnglishText
    ? {
        dir: 'ltr' as const,
        lang: 'en' as const,
        style: englishIsolateStyle,
      }
    : {};

  return (
    <>
      <style>{`
        @keyframes blissnet-loading-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes blissnet-loading-glow-breathe {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 0.8; }
        }
        @keyframes blissnet-loading-ring-pulse {
          0%, 100% { opacity: 0.18; transform: scale(1); }
          50% { opacity: 0.32; transform: scale(1.03); }
        }
        .blissnet-loading-breathe {
          animation: blissnet-loading-breathe 3.2s ease-in-out infinite;
          will-change: transform;
        }
        .blissnet-loading-glow {
          animation: blissnet-loading-glow-breathe 3.2s ease-in-out infinite;
          will-change: opacity;
        }
        .blissnet-loading-ring {
          animation: blissnet-loading-ring-pulse 3.2s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .blissnet-loading-ring-delay {
          animation-delay: 0.4s;
        }
        @media (prefers-reduced-motion: reduce) {
          .blissnet-loading-breathe,
          .blissnet-loading-glow,
          .blissnet-loading-ring {
            animation: none !important;
          }
        }
      `}</style>

      <div
        {...rootLocaleProps}
        className={`fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-[#050508] px-6 pt-[max(1.5rem,env(safe-area-inset-top,0px))] pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] pl-[max(1.5rem,env(safe-area-inset-left,0px))] pr-[max(1.5rem,env(safe-area-inset-right,0px))] ${className}`.trim()}
      >
        <div
          className="blissnet-loading-glow pointer-events-none absolute left-1/2 top-[42%] h-[min(90vw,28rem)] w-[min(90vw,28rem)] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(99,70,220,0.35) 0%, rgba(59,40,160,0.18) 38%, rgba(5,5,8,0) 70%)',
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 flex w-full max-w-md flex-col items-center text-center">
          <div className="blissnet-loading-breathe relative mb-10 flex items-center justify-center sm:mb-12">
            <div
              className="blissnet-loading-ring pointer-events-none absolute h-[148%] w-[148%] rounded-full border border-indigo-400/20"
              aria-hidden="true"
            />
            <div
              className="blissnet-loading-ring blissnet-loading-ring-delay pointer-events-none absolute h-[175%] w-[175%] rounded-full border border-violet-400/15"
              aria-hidden="true"
            />

            <div className="relative h-32 w-32 overflow-hidden rounded-[28%] shadow-[0_0_48px_rgba(120,80,255,0.45)] sm:h-36 sm:w-36 md:h-40 md:w-40">
              <BrandLogo
                size={160}
                alt="BlissNet"
                className="h-full w-full rounded-[28%] object-contain"
              />
            </div>
          </div>

          <h1
            className="mb-3 text-3xl font-semibold tracking-tight text-white sm:mb-4 sm:text-4xl"
            {...englishTextProps}
          >
            {title}
          </h1>

          <div role="status" aria-live="polite" className="w-full">
            <p className={messageClassName} {...englishTextProps}>
              {message}
            </p>

            {showSpinner && (
              <>
                <div
                  className="mx-auto mt-8 h-8 w-8 animate-spin rounded-full border-2 border-pink-500/25 border-t-pink-500 shadow-[0_0_12px_rgba(236,72,153,0.45)] motion-reduce:animate-none sm:mt-10"
                  aria-hidden="true"
                />
                <span className="sr-only">{loadingLabel}</span>
              </>
            )}
          </div>

          {mode === 'slow' && actions ? (
            <div className="mt-8 flex w-full max-w-xs flex-col gap-3 sm:mt-10">{actions}</div>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default BlissNetLoadingScreen;
